/**
 * Staff Login Page
 * Universal 4-digit PIN login for ALL staff roles:
 * - Waiter -> /waiter dashboard
 * - Manager -> /restaurant dashboard  
 * - Cashier -> /restaurant/pos
 * - Kitchen -> /restaurant/kitchen
 * - Driver -> /restaurant/drivers
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { 
  Users, Loader2, ArrowLeft, ChefHat, UserCog, 
  CreditCard, Truck, Coffee
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Role information for display
const ROLE_INFO = {
  waiter: { 
    icon: Coffee, 
    label: 'Waiter', 
    color: 'text-blue-600',
    description: 'Table service & orders'
  },
  restaurant_manager: { 
    icon: UserCog, 
    label: 'Manager', 
    color: 'text-purple-600',
    description: 'Full restaurant access'
  },
  restaurant_cashier: { 
    icon: CreditCard, 
    label: 'Cashier', 
    color: 'text-green-600',
    description: 'POS & payments'
  },
  restaurant_kitchen: { 
    icon: ChefHat, 
    label: 'Kitchen', 
    color: 'text-orange-600',
    description: 'Kitchen display system'
  },
  restaurant_driver: { 
    icon: Truck, 
    label: 'Driver', 
    color: 'text-indigo-600',
    description: 'Delivery management'
  }
};

export default function StaffLogin() {
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  const handlePinChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length <= 4) {
      setPin(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (pin.length !== 4) {
      toast.error('Please enter your 4-digit PIN');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/staff/login`, { code: pin });
      const { access_token, user, redirect } = response.data;
      
      // Store token and user using loginWithToken from AuthContext
      loginWithToken(user, access_token);
      
      // Show success with role info
      const roleInfo = ROLE_INFO[user.role] || { label: user.role };
      toast.success(`Welcome back, ${user.name}!`, {
        description: `Logged in as ${roleInfo.label}`
      });
      
      // Brief display then redirect
      setLoggedInUser(user);
      setTimeout(() => {
        navigate(redirect || '/restaurant');
      }, 1500);
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid PIN code';
      toast.error(message);
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  // Show welcome screen after successful login
  if (loggedInUser) {
    const roleInfo = ROLE_INFO[loggedInUser.role] || { 
      icon: Users, 
      label: loggedInUser.role, 
      color: 'text-slate-600' 
    };
    const Icon = roleInfo.icon;
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className={`w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4`}>
              <Icon className={`w-10 h-10 text-green-600`} />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Welcome, {loggedInUser.name}!
            </h2>
            <Badge className={`${roleInfo.color} bg-slate-100 mb-4`}>
              {roleInfo.label}
            </Badge>
            <p className="text-slate-500">Redirecting to your dashboard...</p>
            <Loader2 className="w-6 h-6 animate-spin mx-auto mt-4 text-orange-500" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4">
      <div className="w-full max-w-md">
        {/* Back to main site */}
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-4 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Main Site
        </Button>

        <Card className="shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto bg-orange-100 rounded-2xl flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-orange-600" />
            </div>
            <CardTitle className="text-2xl">Staff Login</CardTitle>
            <CardDescription>Enter your 4-digit PIN to access your dashboard</CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* PIN Input */}
              <div className="space-y-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[A-Za-z0-9]*"
                  maxLength={4}
                  value={pin}
                  onChange={handlePinChange}
                  placeholder="Enter 4-digit PIN"
                  className="text-center text-3xl tracking-[0.5em] h-16 font-mono"
                  autoFocus
                  data-testid="staff-pin-input"
                />
                <p className="text-center text-sm text-slate-500">
                  {pin.length}/4 digits
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-12 text-lg btn-primary"
                disabled={loading || pin.length !== 4}
                data-testid="staff-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </Button>
            </form>

            {/* Role Legend */}
            <div className="mt-8 pt-6 border-t">
              <p className="text-xs text-slate-500 text-center mb-4">
                This login works for all staff roles:
              </p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(ROLE_INFO).map(([role, info]) => {
                  const Icon = info.icon;
                  return (
                    <div 
                      key={role} 
                      className="flex items-center gap-2 text-xs text-slate-600 p-2 bg-slate-50 rounded"
                    >
                      <Icon className={`w-4 h-4 ${info.color}`} />
                      <span>{info.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Waiter-specific link */}
            <div className="mt-4 text-center">
              <Button
                variant="link"
                onClick={() => navigate('/waiter-login')}
                className="text-sm text-slate-500 hover:text-orange-600"
              >
                Waiter? Use dedicated waiter login →
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Help text */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Don't have a PIN? Contact your restaurant manager.
        </p>
      </div>
    </div>
  );
}
